import { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiSearch, FiLink2 } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import storageService from '../../services/storageService';

/**
 * Componente para gestionar relaciones entre pasajes bíblicos
 * - Usuario registrado: guarda localmente (IndexedDB)
 * - Lector con permisos: puede sincronizar con backend
 */
export default function BibleRelations() {
  const { user } = useAuth();
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    fromReference: '',
    toReference: '',
    relationType: 'paralelo',
    description: '',
    tags: ''
  });

  const relationTypes = [
    { value: 'paralelo', label: '↔️ Pasaje Paralelo', desc: 'Textos que cuentan la misma historia' },
    { value: 'cumplimiento', label: '✅ Cumplimiento', desc: 'Profecía y su cumplimiento' },
    { value: 'referencia', label: '📖 Referencia Cruzada', desc: 'Cita o alusión a otro pasaje' },
    { value: 'contraste', label: '⚖️ Contraste', desc: 'Textos con ideas contrastantes' },
    { value: 'ampliacion', label: '🔍 Ampliación', desc: 'Un pasaje amplía o explica otro' },
    { value: 'tipologia', label: '🎭 Tipología', desc: 'Tipo y antitipo' },
    { value: 'tematico', label: '📌 Temático', desc: 'Relacionados por tema común' }
  ];

  useEffect(() => {
    if (user) {
      loadRelations();
    }
  }, [user]);

  const loadRelations = async () => {
    setLoading(true);
    try {
      await storageService.init();
      // Filtrar por usuario actual
      const allRelations = user?.id 
        ? await storageService.bibleRelations.obtenerPorUsuario(user.id)
        : await storageService.bibleRelations.obtenerTodas();
      console.log('🔗 Relaciones cargadas:', allRelations.length);
      setRelations(allRelations);
    } catch (error) {
      console.error('Error cargando relaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fromReference || !formData.toReference) {
      alert('Ambas referencias son obligatorias');
      return;
    }

    try {
      const relationData = {
        // Formato simple
        fromReference: formData.fromReference,
        toReference: formData.toReference,
        relationType: formData.relationType,
        description: formData.description,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        
        // Formato compatible con BibleRelationModal (para que aparezcan en el lector)
        verseFrom: formData.fromReference,
        verseTo: formData.toReference,
        content: formData.description,
        keywords: formData.tags,
        title: `Relación: ${formData.fromReference}`,
        
        userId: user?.id,
        createdAt: editingId ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await storageService.bibleRelations.actualizar(editingId, relationData);
        console.log('✅ Relación actualizada');
      } else {
        await storageService.bibleRelations.agregar(relationData);
        console.log('✅ Relación creada');
      }

      setFormData({ fromReference: '', toReference: '', relationType: 'paralelo', description: '', tags: '' });
      setEditingId(null);
      setShowForm(false);
      await loadRelations();
    } catch (error) {
      console.error('Error guardando relación:', error);
      alert('Error al guardar la relación');
    }
  };

  const handleEdit = (relation) => {
    setFormData({
      fromReference: relation.fromReference || relation.verseFrom || '',
      toReference: relation.toReference || relation.verseTo || '',
      relationType: relation.relationType || relation.relationTypeId || 'paralelo',
      description: relation.description || relation.content || '',
      tags: Array.isArray(relation.tags) 
        ? relation.tags.join(', ') 
        : (typeof relation.keywords === 'string' 
          ? relation.keywords 
          : (Array.isArray(relation.keywords) ? relation.keywords.join(', ') : ''))
    });
    setEditingId(relation.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta relación?')) return;

    try {
      await storageService.bibleRelations.eliminar(id);
      console.log('✅ Relación eliminada');
      await loadRelations();
    } catch (error) {
      console.error('Error eliminando relación:', error);
      alert('Error al eliminar la relación');
    }
  };

  const filteredRelations = relations.filter(relation => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      relation.fromReference?.toLowerCase().includes(search) ||
      relation.toReference?.toLowerCase().includes(search) ||
      relation.description?.toLowerCase().includes(search) ||
      relation.tags?.some(tag => tag.toLowerCase().includes(search))
    );
  });

  // Agrupar por tipo
  const relationsByType = filteredRelations.reduce((acc, r) => {
    const type = r.relationType || 'otro';
    if (!acc[type]) acc[type] = [];
    acc[type].push(r);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiLink2 className="text-blue-600" />
            Relaciones entre Pasajes
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {relations.length} {relations.length === 1 ? 'relación' : 'relaciones'} guardadas
          </p>
        </div>
        <button
          onClick={() => {
            setFormData({ fromReference: '', toReference: '', relationType: 'paralelo', description: '', tags: '' });
            setEditingId(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <FiPlus className="w-5 h-5" />
          Nueva Relación
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? '✏️ Editar Relación' : '➕ Nueva Relación'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData({ fromReference: '', toReference: '', relationType: 'paralelo', description: '', tags: '' });
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desde (Referencia 1) *
                </label>
                <input
                  type="text"
                  value={formData.fromReference}
                  onChange={(e) => setFormData({ ...formData, fromReference: e.target.value })}
                  placeholder="Ej: Génesis 1:1-3"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hacia (Referencia 2) *
                </label>
                <input
                  type="text"
                  value={formData.toReference}
                  onChange={(e) => setFormData({ ...formData, toReference: e.target.value })}
                  placeholder="Ej: Juan 1:1-3"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Relación *
              </label>
              <select
                value={formData.relationType}
                onChange={(e) => setFormData({ ...formData, relationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {relationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.desc}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Explica cómo se relacionan estos pasajes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiquetas (separadas por comas)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="creación, mesías, profecía..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ fromReference: '', toReference: '', relationType: 'paralelo', description: '', tags: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <FiSave className="w-4 h-4" />
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Barra de búsqueda */}
      {relations.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por referencia, descripción o etiquetas..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Lista de relaciones */}
      {filteredRelations.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {relations.length === 0 ? (
            <>
              <FiLink2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">📭 No tienes relaciones</p>
              <p className="text-sm">Crea tu primera relación haciendo clic en "Nueva Relación"</p>
            </>
          ) : (
            <>
              <p className="text-lg mb-2">🔍 No se encontraron relaciones</p>
              <p className="text-sm">Intenta con otros términos de búsqueda</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(relationsByType).map(([type, typeRelations]) => {
            const typeInfo = relationTypes.find(t => t.value === type) || { label: '🔗 Otro', desc: 'Otro tipo' };
            return (
              <div key={type}>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  {typeInfo.label} ({typeRelations.length})
                </h3>

                <div className="space-y-3">
                  {typeRelations.map((relation) => (
                    <div
                      key={relation.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-r from-blue-50 to-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded">
                              {relation.fromReference || relation.verseFrom || `${relation.book} ${relation.chapter}:${relation.verseStart}`}
                            </span>
                            <span className="text-2xl">→</span>
                            <span className="text-sm font-semibold text-green-600 bg-green-100 px-3 py-1 rounded">
                              {relation.toReference || relation.verseTo || 'N/A'}
                            </span>
                          </div>

                          {(relation.description || relation.content) && (
                            <p className="text-gray-700 mb-2">{relation.description || relation.content}</p>
                          )}

                          {relation.tags && relation.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {relation.tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {relation.createdAt && (
                            <div className="text-xs text-gray-500 mt-2">
                              Creada el {new Date(relation.createdAt).toLocaleString('es-ES')}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(relation)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Editar"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(relation.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Eliminar"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Nota informativa */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>💡 Tip:</strong> Las relaciones te ayudan a conectar pasajes bíblicos para un estudio más profundo. 
          Usa diferentes tipos para organizar mejor tus conexiones (paralelos, profecías cumplidas, referencias cruzadas, etc.).
        </p>
      </div>
    </div>
  );
}


